import _ from 'underscore';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { CampaignTypes } from '@bingads-webui-campaign-react/constants';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { OverviewCard } from '@bingads-webui-campaign-react/overview-card';
import { PageReady } from '@bingads-webui/page-ready';
import { WaterfallFilterStorage, WaterfallFilterType, waterfallDateFilterProcessor } from '@bingads-webui-campaign/filter-storage';
import { getFilteredCampaignTypeFromWaterfallFilter } from '@bingads-webui-campaign-react/shell-ui-common';
import preference from '@bingads-webui-component-legacy/preference/service/index';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { CampaignStatusSummaryCards } from '@bingads-webui-campaign-react/campaign-status-summary-cards';
import { useIsPreferenceInitialized } from '@bingads-webui-campaign/fluent-page-data';

import { getDefaultStyles } from '../style/legacy/styles';
import { usePageStyles } from '../style/styles';
import { useOverviewWindowSizeChange } from '../use-overview-window-size-change';
import { getDefaultCardState } from './default-cards-state';
import { useInitializeBackboneView } from './use-initialize-backbone-view';
import { preferenceName, dashboardPreferenceKey } from '../const';

const campaignStateCard = 'CampaignStatusSummaryCards-CampaignState';
const budgetCard = 'CampaignStatusSummaryCards-Budget';
const bidStrategyCard = 'CampaignStatusSummaryCards-BidStrategy';
const campaignStatusSummaryCards = [{ id: campaignStateCard }, { id: budgetCard }, { id: bidStrategyCard }];

function PerformanceMaxSingleCampaign(props) {
  const {
    serverContext,
    preferenceService,
    i18n,
    sesId,
    perfMarker,
    isSDK,
  } = props;
  const styles = usePageStyles();
  const isPreferenceInitialized = useIsPreferenceInitialized(preference);

  const [cardViewState, setCardViewState] = useState(props.defaultCardViewState || getDefaultCardState(i18n, serverContext, preferenceService));

  const remainingViewportCards = useRef(!isSDK ?
    [...campaignStatusSummaryCards, ..._.filter(cardViewState, card => card.isInViewportOnPageLoad)]
    : _.filter(cardViewState, card => card.isInViewportOnPageLoad));

  const waterfallFilterStorage = useMemo(() => new WaterfallFilterStorage({ preferenceService }), [preferenceService]);

  // Cannot use useMemo here as it will get stale data
  const dateRange = waterfallDateFilterProcessor(
    waterfallFilterStorage.getFilters([WaterfallFilterType.DateFilter]),
    serverContext
  );
  const filter = waterfallFilterStorage.getFilters([
    WaterfallFilterType.CampaignType,
    WaterfallFilterType.CampaignStatus,
    WaterfallFilterType.AdGroupStatus,
  ]);

  const campaignType = getFilteredCampaignTypeFromWaterfallFilter(filter);

  const contentReady = useCallback((id) => {
    if (!_.isEmpty(remainingViewportCards.current)) {
      remainingViewportCards.current = _.reject(remainingViewportCards.current, card => card.id === id);

      if (_.isEmpty(remainingViewportCards.current)) {
        perfMarker.done();
        PageReady.pageIsReady();
      }
    }
  }, [perfMarker]);

  const responsiveSize = useOverviewWindowSizeChange(cardViewState, setCardViewState);

  _.each(cardViewState, (cardState) => {
    if (cardState.backboneInstance) {
      _.defer(() => {
        cardState.backboneInstance.updateState({
          dateRange, campaignType, filter, responsiveSize,
        });
      });
    }
  });

  const campaignStatusContentReady = useCallback(() => contentReady(campaignStateCard), [contentReady]);
  const budgetContentReady = useCallback(() => contentReady(budgetCard), [contentReady]);
  const bidStrategyContentReady = useCallback(() => contentReady(bidStrategyCard), [contentReady]);

  useInitializeBackboneView({
    isPreferenceInitialized,
    preference,
    preferenceName,
    dashboardPreferenceKey,
    cardViewState,
    setCardViewState,
    dateRange,
    filter,
    newI18n: i18n,
    sesId,
    contentReady,
  });

  return (
    <div className={classNames('pmax-overview-page', styles.main)}>
      {!isSDK &&
        <CampaignStatusSummaryCards
          campaignStatusContentReady={campaignStatusContentReady}
          budgetContentReady={budgetContentReady}
          bidStrategyContentReady={bidStrategyContentReady}
          responsiveSize={responsiveSize}
        />}
      {cardViewState.map((item) => {
        if (item.backboneInstance) {
          item.backboneInstance.trigger('attached'); // needed for perf markers on the backbone cards to work correctly
        }
        const perfMarkerUtils = {
          // we don't have child perfMarker created for this page. So, we are using dummy method for now to unblock.
          willFetchData: _.noop,
          willRender: _.noop,
          done: () => contentReady(item.id),
        };

        return (
          <OverviewCard
            key={item.id}
            name={item.name}
            className={item.className}
            isResizable={item.isResizable}
            currentSize={item.size}
            i18n={i18n}
            CardHeaderContent={item.CardHeaderContent}
            cardHeaderContentProps={item.cardHeaderContentProps}
            CustomCardView={item.CustomCardView}
          >
            {
              item.backboneInstance &&
              <div className={classNames('overview-card-backbone-instance')}>
                <BackboneInstanceWrapper managed instance={item.backboneInstance} />
              </div>
            }
            {
              !item.backboneInstance && item.reactView &&
              <item.reactView
                i18n={i18n}
                dateRange={dateRange}
                filter={filter}
                serverContext={serverContext}
                preferenceService={preferenceService}
                sesId={sesId}
                perfMarkerUtils={perfMarkerUtils}
                responsiveSize={responsiveSize}
                campaignType={CampaignTypes.PerformanceMax}
                {...(item.viewProps || {})}
              />
            }
          </OverviewCard>
        );
      })}
    </div>
  );
}

PerformanceMaxSingleCampaign.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({}).isRequired,
    CurrentCustomer: PropTypes.shape({}).isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  defaultCardViewState: PropTypes.arrayOf(PropTypes.shape({})),
  sesId: PropTypes.number,
  perfMarker: PropTypes.shape({
    done: PropTypes.func.isRequired,
  }).isRequired,
  isSDK: PropTypes.bool,
};

PerformanceMaxSingleCampaign.defaultProps = {
  defaultCardViewState: null,
  sesId: -1,
  isSDK: false,
};

export const StyledPerformanceMaxSingleCampaign = withDefaultStyles(PerformanceMaxSingleCampaign, getDefaultStyles);
